import { decodeHtml } from "./domUtils";
import type { PrototypeState } from "./controllerState";
import type { Root } from "./prototypeTypes";
import { routeAuthAction } from "./actionRoutes/authRouteActions";
import { routeBillingAndCommunityActions } from "./actionRoutes/billingCommunityActions";
import { loadViewData } from "./actionRoutes/contextLoaders";
import { activateSourceAttachmentPicker, routeIdeaAndAiActions, runIdeaAndAiChangeHandler } from "./actionRoutes/createActions";
import { routeLearningActions } from "./actionRoutes/learningActions";
import { routeMediaActions } from "./actionRoutes/mediaRouteActions";
import { routeNavigationAction } from "./actionRoutes/navigationActions";
import { routeProfileAndFeedbackActions } from "./actionRoutes/profileFeedbackActions";
import { routeShellAndAuthModals } from "./actionRoutes/shellActions";
import { replayPendingAccessIntent } from './pendingAccessIntent';
import { replayPendingCheckoutIntent } from './pendingCheckoutIntent';
import { presentAccessDecision } from './presentAccessDecision';

export { loadViewData };

function activateNativeFilePicker(normalized: string, root: Root): boolean {
	if (activateSourceAttachmentPicker(normalized, root)) return true;
	if (!normalized.includes('triggerFeedbackFileUpload')) return false;
	(root.querySelector<HTMLInputElement>('#feedbackFileInput'))?.click();
	return true;
}

export async function runPrototypeAction(action: string, root: Root, source: HTMLElement, state: PrototypeState) {
  const normalized = decodeHtml(action);
	// Native file pickers must be requested inside the original trusted click.
	// Do this after the event binding's synchronous access decision but before
	// the ordinary router awaits unrelated action families; iOS WebKit otherwise
	// silently rejects the picker and leaves the active modal looking inert.
	if (activateNativeFilePicker(normalized, root)) return;
	// Safari/iOS requires presentation requests to begin in the same click
	// task. The ordinary router intentionally awaits unrelated action families,
	// which loses activation before WebKit sees PiP or native fullscreen.
	if (normalized.includes('toggleVideoPip') || normalized.includes('toggleVideoFullscreen')) {
		await routeMediaActions(normalized, root, state, source);
		return;
	}
  routeNavigationAction(normalized, root, state, source);
	routeShellAndAuthModals(normalized, root, state, source);
  if (await routeAuthAction(normalized, root, state, async () => {
	await replayPendingAccessIntent(
		{ isLoggedIn: state.isLoggedIn, plan: state.effectiveAccessPlan, userId: state.runtime.userId || undefined },
		async (pendingAction) => {
			const replaySource = root instanceof Document ? root.documentElement : root;
			await runPrototypeAction(pendingAction, root, replaySource, state);
		},
		async (decision, intent) => {
			presentAccessDecision(root, state, decision, {
				rawAction: intent.rawAction,
				policyAction: intent.action,
			});
		},
	);
	await replayPendingCheckoutIntent(state.runtime.userId, async (plan, billingCycle, source) => {
		await previewPendingCheckout(plan, billingCycle, source, root, state);
	});
  })) return;
  if (await routeIdeaAndAiActions(normalized, root, state, source)) return;
  await routeLearningActions(normalized, root, source, state);
	await routeMediaActions(normalized, root, state, source);
  await routeBillingAndCommunityActions(normalized, root, source, state);
  await routeProfileAndFeedbackActions(normalized, root, source, state);
}

async function previewPendingCheckout(
	plan: 'lite' | 'pro' | 'max',
	billingCycle: import('./subscriptionChooserState').BillingCycle,
	source: import('./subscriptionChooserState').ChooserSource,
	root: Root,
	state: PrototypeState,
): Promise<void> {
	const { previewUpgrade } = await import('./commerceController');
	const { failValidation, recordRuntimeError } = await import('./prototypeRuntimeSupport');
	const outcome = await previewUpgrade(plan, root, state, recordRuntimeError, { source, billingCycle });
	if (!outcome.ok && 'message' in outcome) failValidation(outcome.message);
}

/**
 * Change-listener dispatcher. Triggered by PrototypeController when an
 * element with `data-proto-change` fires a `change` event. Currently
 * only the attachment file inputs use this; everything else is on
 * click.
 */
export async function runPrototypeChangeAction(
  action: string,
  event: Event,
  root: Root,
  source: HTMLElement,
  state: PrototypeState,
) {
  const normalized = decodeHtml(action);
  await runIdeaAndAiChangeHandler(normalized, event, root, state);
  void source; // reserved for future change-handlers that need the source element
}
